/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { useRef } from 'react';
import { c as cloneDeep_1 } from '../_chunks/dep-0bc19feb.js';
import useUpdateEffect from '../_util/useUpdateEffect.js';
import { TreeStore } from '../_common/js/tree/tree-store.js';
import { usePersistFn } from '../_util/usePersistFn.js';
import '../_chunks/dep-c3f1141e.js';
import '../_chunks/dep-09050977.js';
import '../_chunks/dep-4b8f240e.js';
import '../_chunks/dep-9acbc163.js';
import '../_chunks/dep-7b52f080.js';
import '../_chunks/dep-f10515e8.js';
import '../_chunks/dep-4941ebe7.js';
import '../_chunks/dep-e9b62d1f.js';
import '../_chunks/dep-88cd50bf.js';
import '../_chunks/dep-dcb5aa2a.js';
import '../_chunks/dep-ee4bf8c2.js';
import '../_chunks/dep-934eda0c.js';
import '../_chunks/dep-7dce2c26.js';
import '../_chunks/dep-12510d64.js';
import '../_chunks/dep-6b568d83.js';
import '../_chunks/dep-304e657b.js';
import '../_chunks/dep-77e34309.js';
import '../_chunks/dep-6506fbc2.js';
import '../_chunks/dep-23d0a0bb.js';
import '../_chunks/dep-c8399d32.js';
import '../_util/useLayoutEffect.js';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-6c5254c6.js';
import '../_util/easing.js';
import '../_chunks/dep-46de7b2e.js';
import '../_chunks/dep-e51b1270.js';
import '../_chunks/dep-0e15088f.js';
import '../_chunks/dep-1cec561b.js';
import '../_chunks/dep-7004b0d9.js';
import '../_chunks/dep-33ffecad.js';
import '../_chunks/dep-c1f93350.js';
import '../_chunks/dep-e73d1b75.js';
import '../_chunks/dep-e285f2f8.js';
import '../_chunks/dep-bb5d7b1e.js';
import '../_chunks/dep-2ffb6f37.js';
import '../_chunks/dep-f721d103.js';
import '../_chunks/dep-4559db64.js';
import '../_chunks/dep-c00532d4.js';
import '../_chunks/dep-08c1f545.js';
import '../_chunks/dep-f4a320cd.js';
import 'mitt';
import '../_common/js/tree/tree-node.js';
import '../_chunks/dep-e0370ebe.js';
import '../_chunks/dep-fd3498b6.js';
import '../_chunks/dep-2acfafca.js';
import '../_chunks/dep-ec16db36.js';
import '../_chunks/dep-a827ffec.js';
import '../_chunks/dep-337b4140.js';
import '../_chunks/dep-ca64038e.js';
import '../_common/js/tree/tree-node-model.js';
import '../_chunks/dep-036ad2cc.js';
import '../_chunks/dep-7dad86aa.js';
import '../_chunks/dep-4f1e1217.js';
import '../_chunks/dep-29cb89ad.js';
import '../_chunks/dep-7b67cdbf.js';
import '../_common/js/log/log.js';
import '../_common/js/log/index.js';

function useStore(props, refresh) {
  var storeRef = useRef();
  var data = props.data,
    keys = props.keys,
    expandAll = props.expandAll,
    expandParent = props.expandParent,
    expanded = props.expanded,
    expandLevel = props.expandLevel,
    expandMutex = props.expandMutex,
    activable = props.activable,
    activeMultiple = props.activeMultiple,
    actived = props.actived,
    disabled = props.disabled,
    draggable = props.draggable,
    checkable = props.checkable,
    value = props.value,
    checkStrictly = props.checkStrictly,
    load = props.load,
    lazy = props.lazy,
    valueMode = props.valueMode,
    filter = props.filter,
    _onLoad = props.onLoad,
    _props$allowFoldNodeO = props.allowFoldNodeOnFilter,
    allowFoldNodeOnFilter = _props$allowFoldNodeO === void 0 ? false : _props$allowFoldNodeO;
  var handleUpdate = usePersistFn(function () {
    refresh();
  });
  var getExpandedArr = function getExpandedArr(arr, store2) {
    var expandedMap = /* @__PURE__ */new Map();
    arr.forEach(function (val) {
      expandedMap.set(val, true);
      if (expandParent) {
        var node = store2.getNode(val);
        node.getParents().forEach(function (tn) {
          expandedMap.set(tn.value, true);
        });
      }
    });
    return Array.from(expandedMap.keys());
  };
  var createStore = function createStore() {
    var store2 = new TreeStore({
      keys: keys,
      activable: activable,
      activeMultiple: activeMultiple,
      checkable: checkable,
      checkStrictly: checkStrictly,
      expandAll: expandAll,
      expandLevel: expandLevel,
      expandMutex: expandMutex,
      expandParent: expandParent,
      disabled: disabled,
      draggable: draggable,
      load: load,
      lazy: lazy,
      valueMode: valueMode,
      filter: filter,
      onLoad: function onLoad(info) {
        var node = info.node;
        _onLoad === null || _onLoad === void 0 ? void 0 : _onLoad({
          node: node.getModel()
        });
      },
      onUpdate: handleUpdate,
      allowFoldNodeOnFilter: allowFoldNodeOnFilter
    });
    var list = cloneDeep_1(data);
    if (!Array.isArray(list)) {
      list = [];
    }
    store2.append(list);
    store2.refreshNodes();
    if (Array.isArray(value)) {
      store2.setChecked(value);
    }
    if (Array.isArray(expanded)) {
      var expandedArr = getExpandedArr(expanded, store2);
      store2.setExpanded(expandedArr);
    }
    if (Array.isArray(actived)) {
      store2.setActived(actived);
    }
    store2.refreshNodes();
    return store2;
  };
  if (!storeRef.current) {
    storeRef.current = createStore();
  }
  var store = storeRef.current;
  useUpdateEffect(function () {
    if (data && Array.isArray(data)) {
      var expanded2 = store.getExpanded();
      var checked = store.getChecked();
      var actived2 = store.getActived();
      store.removeAll();
      store.append(data);
      store.setChecked(checked);
      store.setActived(actived2);
      store.setExpanded(expanded2);
    }
  }, [data, store]);
  useUpdateEffect(function () {
    store.setConfig({
      keys: keys,
      expandAll: expandAll,
      expandLevel: expandLevel,
      expandMutex: expandMutex,
      expandParent: expandParent,
      activable: activable,
      activeMultiple: activeMultiple,
      disabled: disabled,
      checkable: checkable,
      checkStrictly: checkStrictly,
      load: load,
      lazy: lazy,
      valueMode: valueMode
    });
    store.refreshState();
  }, [activable, activeMultiple, checkStrictly, checkable, disabled, expandAll, expandLevel, expandMutex, expandParent, keys, lazy, load, store, valueMode]);
  useUpdateEffect(function () {
    if (Array.isArray(value)) {
      store.replaceChecked(value);
    }
  }, [store, value]);
  useUpdateEffect(function () {
    if (Array.isArray(expanded)) {
      var expandedArr = getExpandedArr(expanded, store);
      store.replaceExpanded(expandedArr);
    }
  }, [expanded, store]);
  useUpdateEffect(function () {
    if (Array.isArray(actived)) {
      store.replaceActived(actived);
    }
  }, [actived, store]);
  useUpdateEffect(function () {
    store.setConfig({
      filter: filter
    });
    store.updateAll();
  }, [filter, store]);
  return storeRef.current;
}

export { useStore };
//# sourceMappingURL=useStore.js.map
